import { WarningOutlined } from "@ant-design/icons";
import { Flex, Modal, Typography, theme } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import FreePlanCard from "../components/subscription/FreePlanCard";
import PremiumPlanCard from "../components/subscription/PremiumPlanCard";
import SupportSection from "../components/subscription/SupportSection";
import { useAuthContext } from "../contexts/auth/useAuthContext";
import { useBillingPortalLinkQuery } from "../graphql/query.billingPortalLink";
import { usePaymentLinkQuery } from "../graphql/query.paymentLink";
import { useNavigate } from "react-router-dom";
import { NEW_CHART } from "../routes";

const { Title, Paragraph } = Typography;

const SubscriptionPage = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { hasSubscription } = useAuthContext();
  const { refetch: getPaymentLink } = usePaymentLinkQuery({ skip: true });
  const { refetch: getBillingPortalLink } = useBillingPortalLinkQuery({
    skip: true,
  });
  const [isGetBillingPortalLinkLoading, setIsGetBillingPortalLinkLoading] =
    useState(false);

  const [isCancelSubscriptionModalOpen, setIsCancelSubscriptionModalOpen] =
    useState(false);
  const [isGetPaymentLinkLoading, setIsGetPaymentLinkLoading] = useState(false);

  const handleFreePlanClick = () => {
    if (hasSubscription) {
      // If the user already has a subscription, open the cancel subscription modal
      setIsCancelSubscriptionModalOpen(true);
      return;
    }
    // else, redirect to the chart creation page
    navigate(NEW_CHART);
  };
  const openBillingPortal = () => {
    setIsGetBillingPortalLinkLoading(true);
    getBillingPortalLink().then((response) => {
      if (response.data?.billingPortalLink) {
        window.location.href = response.data.billingPortalLink.link;
      }
    });
  };

  const handlePremiumPlanClick = () => {
    // Trigger payment link query when premium plan is selected
    if (!hasSubscription) {
      setIsGetPaymentLinkLoading(true);
      getPaymentLink().then((response) => {
        if (response.data?.paymentLink) {
          window.location.href = response.data.paymentLink.link;
        }
      });
      return;
    }
    // If the user already has a subscription, redirect to billing portal
    openBillingPortal();
  };

  return (
    <>
      <Flex
        vertical
        gap={token.marginLG}
        align="center"
        style={{
          height: "100%",
          maxHeight: "calc(100vh - 130px)",
          overflow: "auto",
        }}
      >
        <Typography
          style={{ textAlign: "center", marginBottom: token.marginSM }}
        >
          {!hasSubscription && (
            <>
              <Title level={1}>{t("subscription_page.choose_your_plan")}</Title>
              <Paragraph>{t("subscription_page.select_plan")}</Paragraph>
            </>
          )}
          {hasSubscription && (
            <>
              <Title level={1}>{t("subscription_page.manage_your_plan")}</Title>
              <Paragraph>{t("subscription_page.current_plan")}</Paragraph>
            </>
          )}
        </Typography>

        <Flex
          wrap="wrap"
          gap={token.marginLG}
          justify="center"
          style={{ maxWidth: 1200, width: "100%" }}
        >
          <FreePlanCard
            onButtonClick={handleFreePlanClick}
            isCurrentPlan={!hasSubscription}
          />
          <PremiumPlanCard
            onButtonClick={handlePremiumPlanClick}
            isLoading={isGetBillingPortalLinkLoading || isGetPaymentLinkLoading}
            isCurrentPlan={hasSubscription}
          />
        </Flex>

        <SupportSection />
      </Flex>
      {isCancelSubscriptionModalOpen && (
        <Modal
          title={
            <Flex gap={8}>
              <WarningOutlined style={{ color: token.colorError }} />
              {t("subscription_page.cancel_subscription")}
            </Flex>
          }
          open={true}
          onOk={openBillingPortal}
          okText={t("yes")}
          cancelText={t("no")}
          confirmLoading={isGetBillingPortalLinkLoading}
          onCancel={() => setIsCancelSubscriptionModalOpen(false)}
          cancelButtonProps={{
            disabled: isGetBillingPortalLinkLoading,
          }}
          okButtonProps={{
            danger: true,
          }}
          centered={true}
          closable={false}
        >
          <p>{t("subscription_page.confirm_cancel_subscription")}</p>
        </Modal>
      )}
    </>
  );
};

export default SubscriptionPage;
